import { useState, useEffect, useRef, useMemo } from 'react';
import { CheckboxField } from '@/components/formParts';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { terms, jrWestTerms } from '@/data';
// import { terms, privacyPolicy} from '@/data';

// バリデーション用のスキーマ設定
const schema = z.object({
  terms_checked: z.boolean().refine((val) => val !== false, { message: "同意チェックは必須です" }),
  jr_terms_checked: z.boolean().refine((val) => val !== false, { message: "同意チェックは必須です" }),
  // privacy_checked: z.boolean().refine((val) => val !== false, { message: "同意チェックは必須です" }),
});

// バリデーション用の型定義
export type RegisterAgreementFormData = z.infer<typeof schema>;

// テキスト内のURLを自動検出してリンク化
// [表示テキスト](URL) 形式 → テキストのみ表示してリンク
// 裸のURL → URLをそのまま表示してリンク
const renderWithLinks = (text: string) => {
  const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)|(https?:\/\/[^\s）)]+)/g;
  return text.split('\n').map((line, lineIdx) => {
    const parts: (string | React.ReactElement)[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    linkRegex.lastIndex = 0;
    while ((match = linkRegex.exec(line)) !== null) {
      if (match.index > lastIndex) {
        parts.push(line.slice(lastIndex, match.index));
      }
      if (match[1] && match[2]) {
        // [テキスト](URL) 形式
        parts.push(
          <a key={`${lineIdx}-${match.index}`} href={match[2]} target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc', textDecoration: 'underline' }}>{match[1]}</a>
        );
      } else if (match[3]) {
        // 裸のURL
        parts.push(
          <a key={`${lineIdx}-${match.index}`} href={match[3]} target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc', textDecoration: 'underline' }}>{match[3]}</a>
        );
      }
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < line.length) {
      parts.push(line.slice(lastIndex));
    }
    return (
      <span key={lineIdx}>
        {parts.length > 0 ? parts : line}
        {'\n'}
      </span>
    );
  });
};

// 規約ボックスの共通スタイル
const termsBoxStyle = (enabled: boolean): React.CSSProperties => ({
  width: '100%',
  height: '12em',
  overflowY: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  lineHeight: '2',
  font: 'inherit',
  border: '1px solid var(--input-border-color)',
  borderRadius: '.4rem',
  backgroundColor: enabled ? 'var(--input-container-color)' : '#f0f0f0',
  padding: '1em',
  fontSize: 'inherit',
});

// 規約ボックスを最後までスクロールしたかを監視する
const useScrolledToBottom = (ref: React.RefObject<HTMLDivElement | null>) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // スクロールが発生しない高さの場合は、読了済みとして扱う
    if (element.scrollHeight <= element.clientHeight) {
      setScrolled(true);
      return;
    }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = element;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5;
      if (isAtBottom) {
        setScrolled(true);
        element.removeEventListener('scroll', handleScroll);
      }
    };

    element.addEventListener('scroll', handleScroll);

    return () => {
      element.removeEventListener('scroll', handleScroll);
    };
  }, [ref]);

  return scrolled;
};


// フォームコンポーネント
const RegisterAgreementForm = ( { enabled, onValidationChange, shouldSubmit }: {
    enabled: boolean;
    onValidationChange: (data: {isValid: boolean, formData: RegisterAgreementFormData | undefined}) => void;
    shouldSubmit: boolean;
  }
) => {

  const { register, watch, formState: { errors, isValid }, trigger } = useForm<RegisterAgreementFormData>({
    mode: 'onTouched',
    resolver: zodResolver(schema),
  });


  // 親にバリデーション状態を通知
  useEffect(() => {
      onValidationChange({isValid, formData: isValid ? watch() : undefined});
  }, [isValid]);

  // 親からのsubmit要求があったらバリデーションを実行
  useEffect(() => {
    if( shouldSubmit ) {
      trigger();
    }
  }, [shouldSubmit]);

  const renderTermsWithLinks = useMemo(() => renderWithLinks(terms), []);
  const renderJrWestTermsWithLinks = useMemo(() => renderWithLinks(jrWestTerms), []);

  // スクロール監視
  const termsRef = useRef<HTMLDivElement>(null);
  const jrTermsRef = useRef<HTMLDivElement>(null);
  const termsScrolled = useScrolledToBottom(termsRef);
  const jrTermsScrolled = useScrolledToBottom(jrTermsRef);

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <div className="m-form-item">
          <div className="m-form-item__header">
            <p className="m-form-item__caption">利用規約<span className="required">*</span></p>
          </div>
          <div className="m-form-item__input">
            <div className="m-form-item__input__container">
              <div ref={termsRef} style={termsBoxStyle(enabled)}>
                {renderTermsWithLinks}
              </div>
            </div>
          </div>
        </div>
        <p className="m-form-note">※全文をお読みいただいたうえで、チェックボックスにチェックしてください。</p>
        <CheckboxField
          options={[{ label: '利用規約及び第18条の個人情報の取扱を確認、理解した上で同意する', value: '' }]}
          register={register('terms_checked')}
          errors={errors}
          hasRequiredLabel={true}
          disabled={!enabled || !termsScrolled}
        />
      </div>
      <div className="flex flex-col gap-2">
        <div className="m-form-item">
          <div className="m-form-item__header">
            <p className="m-form-item__caption">JR西日本プラン利用特約<span className="required">*</span></p>
          </div>
          <div className="m-form-item__input">
            <div className="m-form-item__input__container">
              <div ref={jrTermsRef} style={termsBoxStyle(enabled)}>
                {renderJrWestTermsWithLinks}
              </div>
            </div>
          </div>
        </div>
        <p className="m-form-note">※全文をお読みいただいたうえで、チェックボックスにチェックしてください。</p>
        <CheckboxField
          options={[{ label: 'JR西日本プラン利用特約及び第11条の個人情報の取扱を確認、理解した上で同意する', value: '' }]}
          register={register('jr_terms_checked')}
          errors={errors}
          hasRequiredLabel={true}
          disabled={!enabled || !jrTermsScrolled}
        />
      </div>
      {/* <div className="flex flex-col gap-2">
        <TextareaField label="プライバシーポリシー" value={privacyPolicy} rows={6} ref={privacyRef} readOnly={true} disabled={!enabled} hasRequiredLabel={true} />
        <p className="m-form-note">※全文をお読みいただいたうえで、チェックボックスにチェックしてください。</p>
        <CheckboxField
          options={[{ label: 'プライバシーポリシーに同意します', value: '' }]}
          register={register('privacy_checked')}
          errors={errors}
          hasRequiredLabel={true}
          disabled={!enabled || !privacyScrolled}
        />
      </div> */}
    </div>
  );
}

export default RegisterAgreementForm;
